import React, { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import '../styles/reviewassigned.css';
import IssuedCertificates from "../components/issuedCertificates";
import { ROLES } from "../constants/textconstants";
import { BackArrow, Edit } from "../constants";
import { get, put, del } from "../config/api";
import AssignCertificateModal from "../components/assignCertficateModal";
import { Delete } from "../constants";
import { showError, showToast } from "../store/slices/formconfig";
import { formatDateToDDMMYYYY } from "../utils/dateUtils";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import ConfirmationModal from "../components/confirmationModal";
import { Loader } from "../components/loader";

interface UserData {
  userId: string;
  role: string;
  squads?: { code: string }[];
  units?: { code: string }[];

}

interface AssignedRequest {
  booklet_id: string;

}

interface LocationState {
  request?: AssignedRequest;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ReviewAssignedCertificates: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { assignedId } = useParams<{ assignedId: string }>();

  const [assignedDetails, setAssignedDetails] = useState<any>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [issuedCertificatesData, setIssuedCertificatesData] = useState<any>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [booklets, setBooklets] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const userData: UserData = useSelector((state: any) => state.formConfig.user);


  const fetchAsignedUsers = async () => {
      try {
        let assignedUsers = await get(
          `${API_ENDPOINTS.USER_SUBORDINATES(userData.userId)}`
        );
        if (assignedUsers) {
          setUsers(assignedUsers);
        }
  
      } catch (error) {
         dispatch(showError({ message: `Failed to fetch users, due to some technical issue, please try again`, visible: true }))
              
        console.error("Failed to fetch assigned users:", error);
      }
    };

  const fetchBooklets = async () => {
    try {
      let bookletData = await get(
        `${API_ENDPOINTS.ASSIGNABLE_CERTIFICATE_BOOKLETS_BY_USER(userData.userId)}`
      );
      if (bookletData) {
        setBooklets(bookletData);
      }
    } catch (error) {
      dispatch(showError({ message: `Failed to fetch booklets, due to some technical issue, please try again`, visible: true }))
      console.error("Failed to fetch booklets:", error);
    }
  };




  const handleEditClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setShowConfirmationModal(true);
  }, []);

  const handleCloseConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleModalSubmit = useCallback(async (formData: any) => {
    const assignedRequest = (location.state as LocationState)?.request;

    if (assignedRequest) {
      let DataToSubmit = {
        issued_by: {
          user_id: userData.userId,
        },
        booklet_prefix: formData.certificatePrefix || "",
        book_start_no: formData.from,
        book_end_no: formData.to,
        issued_to_location_code: formData.location_code,
      }

      setIsSaving(true);
      try {
        const response = await put(`/certificate-booklets/${assignedRequest?.booklet_id}`, DataToSubmit);
        if (response) {
          navigate(-1)
        } else {
          dispatch(showError({ message: `Failed Assigning request. Please try again. ${response.message ? response.message : ''}`, visible: true }))
        }
      } catch (error: any) {
        dispatch(showError({ message: `Failed Assigning request. Please try again. ${error.message ? error.message : ''}`, visible: true }))
        console.error('API call failed:', error);
      } finally {
        setIsSaving(false);
      }
    }
    // After a successful API call:
    handleModalClose();
    fetchAssignedDataAndUsers();

  }, [assignedId, location.state, userData.userId, dispatch, handleModalClose]);

  const handleConfirmDelete = useCallback(async () => {
    
    const bookletIdToDelete = location.state.request.booklet_id;
    if (!bookletIdToDelete) {
      dispatch(showError({ message: "Booklet ID not found for deletion.", visible: true }));
      return;
    }

    try {
      const response = await del(`${API_ENDPOINTS.CERTIFICATE_BOOKLETS}/${bookletIdToDelete}`);
      if (response) {
        dispatch(showToast({ message: "Certificate booklet deleted successfully!", type: "success" }));
        navigate(-1); // Go back to the previous page (e.g., AssignedCertificates list)
      } else {
        dispatch(showError({ message: `Failed to delete booklet. Please try again. ${response.message ? response.message : ''}`, visible: true }));
      }
    } catch (error: any) {
      dispatch(showError({ message: `Failed to delete booklet. Please try again. ${error.message ? error.message : ''}`, visible: true }));
      console.error('API call failed:', error);
    }
    setShowConfirmationModal(false);
  }, [assignedId]);



  const fetchAssignedDataAndUsers = useCallback(async () => {

    if (!userData || !userData.role) return;

    const assignedRequest = (location.state as LocationState)?.request;
    if (assignedRequest) {
      setAssignedDetails(assignedRequest);
    }
    await Promise.all([fetchAsignedUsers(), fetchBooklets()]);
  }, [userData, location.state, dispatch]);



  useEffect(() => {

    fetchAssignedDataAndUsers();
  }, [fetchAssignedDataAndUsers]);


  const prepopulatedData = useMemo(() => { //
    if (!assignedDetails.book_start_no) return null;

    return {
      // Construct the assignee string in the format "user_id-code"
      // Prioritize issued_to_id if available, otherwise use user_id from issued_to object
      assignee:
        (assignedDetails.issued_to_id
          ? assignedDetails.issued_to_id
          : assignedDetails.issued_to?.user_id),
      from: parseInt(assignedDetails.book_start_no),
      to: parseInt(assignedDetails.book_end_no),
      certificatePrefix: assignedDetails.booklet_prefix,
    };
  }, [assignedDetails]);

let assignedRequest = location.state.request.assignedRequest;

  return (
    <div style={{ background: 'rgb(236, 239, 241)', paddingTop: 80, paddingBottom: 80 }}>
      {isSaving && <Loader />}

      <div className="review-assigned-certificates">
        <button className="back-button" onClick={() => navigate(-1)}>
          <img src={BackArrow} height={20} style={{ marginRight: 10 }} alt="Back" /> Review Assigned Certificates
        </button>

        {assignedDetails && (
          <div className="assigned-summary">
            <div><span className="subtitle">{userData.role === ROLES.APPROVER?'Territory': 'Squad'}:</span> <span className="reviewdata">{assignedDetails.issuedLocation}</span></div>
            <div><span className="subtitle">Assigned To:</span> <span className="reviewdata">{assignedDetails && assignedDetails.issued_to && assignedDetails.issued_to.username}</span></div>
            <div><span className="subtitle">Certificate no. Range:</span> <span className="reviewdata">{assignedDetails.booklet_prefix}{assignedDetails.book_start_no} - {assignedDetails.booklet_prefix}{assignedDetails.book_end_no}</span></div>
            <div>
              <span className="subtitle">Assigned Date:</span><span className="reviewdata"> {formatDateToDDMMYYYY(assignedDetails.issued_date)}</span>
            </div>
            <div>
              <span className="subtitle">Issued Qty:</span><span className="reviewdata"> {assignedDetails.noOfCertificatesIssued}/{assignedDetails.totalNoOfCertificates}</span>
            </div>


            <div style={{display:'flex', flexDirection:'row'}}>
               <span className="edit-icon" onClick={handleEditClick}>
              <img src={Edit} height={20}/>
            </span>
            {assignedDetails.noOfCertificatesIssued === 0  && (
              <span className="edit-icon" onClick={handleDeleteClick}>
                <img src={Delete} style={{marginTop:-2}} height={25} alt="Delete" />
              </span>
            )}
            </div>
          </div>
        )}

        <IssuedCertificates
         showHeaderActions={true}
          from='assigned'
          bookletId={location.state.request.booklet_id || ''} />
      </div>

      {(userData.role === ROLES.LICENSE_MANAGER || userData.role === ROLES.APPROVER) && isModalOpen && (
        <AssignCertificateModal
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          users={users}
          booklets={booklets}
          prepopulatedData={prepopulatedData}
        />
      )}

      {showConfirmationModal && (
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={handleCloseConfirmationModal}
          onConfirm={handleConfirmDelete}
          message="This action cannot be undone. Do you want to proceed?"
        />
      )}
    </div>
  );
};

export default ReviewAssignedCertificates;